//
// Helper function to normalize API response - converts PeopleId/PeopleInfo to AlienId/AlienInfo
// for backward compatibility with existing components.
//
export function normalizePeopleResponse(data) {
    if (!data) return data;
    
    // Handle array responses
    if (Array.isArray(data)) {
        return data.map(item => normalizePeopleResponse(item));
    }
    
    // Handle object responses
    if (typeof data === 'object') {
        const normalized = { ...data };
        
        // Convert PeopleId to alienId (keep both for compatibility)
        // Handle both camelCase (peopleId) and PascalCase (PeopleId)
        if (normalized.peopleId !== undefined) {
            normalized.alienId = normalized.peopleId;
        } else if (normalized.PeopleId !== undefined) {
            normalized.peopleId = normalized.PeopleId;
            normalized.alienId = normalized.PeopleId;
        }
        
        // Convert NextCheckinDate to nextCheckInDue for backward compatibility
        if (normalized.nextCheckinDate !== undefined) {
            normalized.nextCheckInDue = normalized.nextCheckinDate;
        } else if (normalized.NextCheckinDate !== undefined) {
            normalized.nextCheckinDate = normalized.NextCheckinDate;
            normalized.nextCheckInDue = normalized.NextCheckinDate;
        }

        if (normalized.missedCheckInTypeId === undefined && normalized.MissedCheckInTypeId !== undefined) {
            normalized.missedCheckInTypeId = normalized.MissedCheckInTypeId;
        }

        if (normalized.missedCourtCheckIns === undefined && normalized.MissedCourtCheckIns !== undefined) {
            normalized.missedCourtCheckIns = normalized.MissedCourtCheckIns;
        }

        if (normalized.checkInTypeId === undefined && normalized.CheckInTypeId !== undefined) {
            normalized.checkInTypeId = normalized.CheckInTypeId;
        }

        if (normalized.isTransient === undefined && normalized.IsTransient !== undefined) {
            normalized.isTransient = normalized.IsTransient;
        }

        // Alias createdAt → createdDate (DTO was renamed CreatedDate → CreatedAt in PR 1142)
        if (normalized.createdAt !== undefined && normalized.createdDate === undefined) {
            normalized.createdDate = normalized.createdAt;
        }

        // Convert PeopleName to alienName for backward compatibility
        if (normalized.peopleName !== undefined) {
            normalized.alienName = normalized.peopleName;
        } else if (normalized.PeopleName !== undefined) {
            normalized.peopleName = normalized.PeopleName;
            normalized.alienName = normalized.PeopleName;
        }
        
        // Convert PeopleInfo structure to AlienInfo structure
        if (normalized.peopleVisaMetadata) {
            normalized.aNumber = normalized.peopleVisaMetadata.aNumber;
            normalized.countryOfOrigin = normalized.peopleVisaMetadata.countryOfOrigin;
            normalized.portOfEntry = normalized.peopleVisaMetadata.portOfEntry;
            normalized.arrivalDate = normalized.peopleVisaMetadata.arrivalDate;
            normalized.reasonForEntry = normalized.peopleVisaMetadata.reasonForEntry;
        }
        
        // Convert PeopleLocalContact to AlienLocalContact
        if (normalized.localContacts && Array.isArray(normalized.localContacts)) {
            normalized.localContacts = normalized.localContacts.map(contact => {
                const normalizedContact = { ...contact };
                if (normalizedContact.peopleId !== undefined) {
                    normalizedContact.alienId = normalizedContact.peopleId;
                }
                return normalizedContact;
            });
        }
        
        // Convert PeopleBiometricVerifications to AlienBiometricVerifications
        if (normalized.peopleBiometricVerifications && Array.isArray(normalized.peopleBiometricVerifications)) {
            normalized.alienBiometricVerifications = normalized.peopleBiometricVerifications.map(bio => {
                const normalizedBio = { ...bio };
                if (normalizedBio.peopleId !== undefined) {
                    normalizedBio.alienId = normalizedBio.peopleId;
                }
                return normalizedBio;
            });
        }
        
        // Handle PeopleInfo nested objects (e.g., in BondApplicationResponse)
        if (normalized.peopleInfo && typeof normalized.peopleInfo === 'object') {
            normalized.peopleInfo = normalizePeopleResponse(normalized.peopleInfo);
            // Also add alienInfo alias for backward compatibility
            normalized.alienInfo = normalized.peopleInfo;
        }
        
        // Handle CheckIns array which might contain PeopleId
        if (normalized.checkIns && Array.isArray(normalized.checkIns)) {
            normalized.checkIns = normalized.checkIns.map(checkIn => normalizePeopleResponse(checkIn));
        }
        
        // Handle nested objects recursively (but avoid Date objects and arrays)
        Object.keys(normalized).forEach(key => {
            if (normalized[key] && typeof normalized[key] === 'object' && !Array.isArray(normalized[key]) && !(normalized[key] instanceof Date)) {
                normalized[key] = normalizePeopleResponse(normalized[key]);
            }
        });
        
        return normalized;
    }
    
    return data;
}

